<template>
  <div class="sm-panel" v-loading="!!loadingCalc || timemachinePlaying">
    <div class="calculations-select">
      <r-text type="caption" style="margin-bottom: 4px;">
        Выберите расчет для отображения результатов
      </r-text>
      <div class="calculations-select-mode">
        <el-radio-group v-model="mode" @change="modeChangeHandler" size="mini">
          <el-radio-button
            v-for="item in modes"
            :key="item.value"
            :label="item.value"
          >
            {{ item.name }}
          </el-radio-button>
        </el-radio-group>
      </div>
      <ul class="calculations-select-list" v-if="calculations.length">
        <li
          :class="getCalcClass(item.id)"
          v-for="item in calculations"
          :key="item.id"
          @click="calcClickHandler(item)"
        >
          <div class="calculations-select-item-info">
            <r-text>
              {{ item.name || "Без имени" }}
            </r-text>
            <r-text type="caption">
              {{ item.calc_type }}
            </r-text>
          </div>
          <div class="calculations-select-item-time">
            <r-text type="caption">
              {{ getCalcTime(item) }}
            </r-text>
          </div>
        </li>
      </ul>
      <div class="calculations-select-nodata" v-else>
        <r-text type="caption">
          Расчеты отсутствуют
        </r-text>
      </div>
    </div>
    <div class="sm-fields" v-if="activeCalc">
      <r-text type="caption" style="margin-bottom: 8px;">
        Отображение значений по атрибуту
      </r-text>
      <div class="sm-fields-container">
        <el-select
          class="r-select"
          v-model="activeField"
          placeholder="Выберите атрибут"
          filterable
          size="default"
          :disabled="!!loadingCalc"
        >
          <el-option
            v-for="item in fields"
            :key="item.name"
            :label="item.name"
            :value="item.name"
          />
        </el-select>
      </div>
    </div>

    <sm-diagram-settings v-if="activeCalc && activeField" />

    <sm-help v-if="activeCalc" style="margin-top: auto;" />
  </div>
</template>

<script>
import moment from "moment";
import SmHelp from "./sm-help";
import SmDiagramSettings from "./sm-panel/sm-diagram-settings";

const modes = [
  { value: "one", name: "Один расчет" },
  { value: "diff", name: "Разница расчетов" }
];

export default {
  components: {
    SmHelp,
    SmDiagramSettings
  },
  data() {
    return {
      model: this.$store.state.scenarioModelling.model,
      loading: false,
      calculations: [],
      fields: [],
      modes
    };
  },
  computed: {
    filteredCalculations() {
      return this.calculations.filter(c => c.end_time);
    },
    activeCalc() {
      return this.$store.state.scenarioModelling.activeCalc;
    },
    diffCalc() {
      return this.$store.state.scenarioModelling.diffCalc;
    },
    loadingCalc() {
      return this.$store.state.scenarioModelling.loadingCalc;
    },
    timemachinePlaying() {
      return this.$store.state.scenarioModelling.timemachinePlaying;
    },
    activeField: {
      get() {
        return this.$store.state.scenarioModelling.activeField;
      },
      set(value) {
        this.$store.commit("CHANGE_SM_CALC_PROP", {
          field: "activeField",
          value
        });
      }
    },
    mode: {
      get() {
        return this.$store.state.scenarioModelling.calcMode;
      },
      set(value) {
        this.$store.commit("CHANGE_SM_CALC_PROP", {
          field: "calcMode",
          value
        });
      }
    }
  },
  methods: {
    async getData() {
      try {
        this.loading = true;
        const { ids } = this.model;
        const id = ids["calculations"];
        const url = `objectInfo/${id}`;
        const { data } = await this.$store.dispatch("REQUEST", { url });

        this.calculations = Object.values(data);
        this.loading = false;
      } catch (error) {
        console.log(error);
        this.loading = false;
      }
    },
    parseTime(time) {
      return moment(time).format("YYYY/MM/DD HH:mm:ss");
    },
    getCalcTime({ start_time, end_time }) {
      return `${this.parseTime(start_time)} - ${this.parseTime(end_time)}`;
    },
    getCalcClass(id) {
      const activeClass = `${this.mode}-active`;

      if (this.mode === "one") {
        return `calculations-select-item ${
          this.activeCalc === id ? activeClass : ""
        }`;
      }

      if (this.activeCalc === id)
        return `calculations-select-item ${activeClass} ${activeClass}-first`;
      if (this.diffCalc === id)
        return `calculations-select-item ${activeClass} ${activeClass}-second`;

      return `calculations-select-item`;
    },
    async getFieldsData() {
      const { ids } = this.model;
      const id = ids["link_calculation_results"];
      const url = `objectFields/${id}`;

      const { data } = await this.$store.dispatch("REQUEST", { url });

      return Object.keys(data)
        .map(name => ({ ...data[name], name }))
        .filter(
          f =>
            (f.type === "float" ||
              f.type === "integer" ||
              f.type === "numeric" ||
              f.type === "decimal") &&
            f.name !== "time_value"
        );
    },
    async getTimeData(calcId) {
      const { ids } = this.model;
      const id = ids["link_calculation_results"];
      const config = {
        unique_values: "time_value",
        where: [
          {
            field: "calculation_id",
            op: "=",
            value: calcId
          }
        ]
      };
      const url = `objectInfo/${id}?config=${JSON.stringify(config)}`;

      const { data } = await this.$store.dispatch("REQUEST", { url });

      return data.length === 1 ? null : data;
    },
    calcClickHandler(calc) {
      if (this.loadingCalc) return;

      if (this.mode === "one") this.changeOneCalculation(calc);
      else this.changeDiffCalculations(calc);
    },
    async changeDiffCalculations(calc) {
      const value = calc.id;
      const {
        activeCalc,
        diffCalc,
        activeField,
        activeTimeValue
      } = this.$store.state.scenarioModelling;

      if (activeCalc === value) return this.modeChangeHandler();

      try {
        if (!activeCalc) {
          this.$store.commit("CHANGE_SM_CALC_PROP", {
            field: "loadingCalc",
            value
          });

          const fields = await this.getFieldsData();
          const times = await this.getTimeData(value);

          this.fields = fields;

          if (times) {
            if (!activeTimeValue) {
              this.$store.commit("CHANGE_SM_CALC_PROP", {
                field: "activeTimeValue",
                value: times[0]
              });
            }
            this.$store.commit("CHANGE_SM_CALC_PROP", {
              field: "timeData",
              value: times
            });
          } else {
            this.$store.commit("CHANGE_SM_CALC_PROP", {
              field: "timeData",
              value: null
            });
            this.$store.commit("CHANGE_SM_CALC_PROP", {
              field: "activeTimeValue",
              value: null
            });
          }

          if (!activeField) {
            this.$store.commit("CHANGE_SM_CALC_PROP", {
              field: "activeField",
              value: fields[0].name
            });
          }

          this.$store.commit("CHANGE_SM_CALC_PROP", {
            field: "loadingCalc",
            value: null
          });
          this.$store.commit("CHANGE_SM_CALC_PROP", {
            field: "activeCalc",
            value
          });
          this.$store.commit("CHANGE_SM_CALC_PROP", {
            field: "activeCalcType",
            value: calc.calc_type
          });
        } else {
          if (value !== diffCalc) {
            this.$store.commit("CHANGE_SM_CALC_PROP", {
              field: "diffCalc",
              value
            });
          } else {
            this.$store.commit("CHANGE_SM_CALC_PROP", {
              field: "diffCalc",
              value: null
            });
          }
        }
      } catch (error) {
        console.log(error);
        this.$store.commit("CHANGE_SM_CALC_PROP", {
          field: "loadingCalc",
          value: null
        });
      }
    },
    async changeOneCalculation(calc) {
      const value = calc.id;
      const {
        activeField,
        activeTimeValue
      } = this.$store.state.scenarioModelling;

      if (this.activeCalc === value) return this.modeChangeHandler();

      try {
        this.$store.commit("CHANGE_SM_CALC_PROP", {
          field: "loadingCalc",
          value
        });

        const fields = await this.getFieldsData();
        const times = await this.getTimeData(value);

        this.fields = fields;

        if (times) {
          if (!activeTimeValue) {
            this.$store.commit("CHANGE_SM_CALC_PROP", {
              field: "activeTimeValue",
              value: times[0]
            });
          }
          this.$store.commit("CHANGE_SM_CALC_PROP", {
            field: "timeData",
            value: times
          });
        } else {
          this.$store.commit("CHANGE_SM_CALC_PROP", {
            field: "timeData",
            value: null
          });
          this.$store.commit("CHANGE_SM_CALC_PROP", {
            field: "activeTimeValue",
            value: null
          });
        }

        if (!activeField) {
          this.$store.commit("CHANGE_SM_CALC_PROP", {
            field: "activeField",
            value: fields[0].name
          });
        }

        this.$store.commit("CHANGE_SM_CALC_PROP", {
          field: "loadingCalc",
          value: null
        });
        this.$store.commit("CHANGE_SM_CALC_PROP", {
          field: "activeCalc",
          value
        });
        this.$store.commit("CHANGE_SM_CALC_PROP", {
          field: "activeCalcType",
          value: calc.calc_type
        });
      } catch (error) {
        console.log(error);
        this.$store.commit("CHANGE_SM_CALC_PROP", {
          field: "loadingCalc",
          value: null
        });
      }
    },
    modeChangeHandler() {
      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "activeCalc",
        value: null
      });
      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "diffCalc",
        value: null
      });
      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "maxType",
        value: "field"
      });
    }
  },
  created() {
    this.getData();
  }
};
</script>

<style lang="scss">
.sm-panel {
  padding: 16px;
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
}

.calculations-select {
  &-mode {
    margin: 8px 0;

    .el-radio-group {
      display: flex;
      width: 100%;
    }

    .el-radio-button {
      flex: 1;
      box-shadow: none !important;

      &__orig-radio:checked + .el-radio-button__inner {
        color: var(--white_white) !important;
        background-color: var(--accent_primary) !important;
        border-color: var(--accent_primary) !important;
      }

      &__inner {
        width: 100%;
        color: var(--text_primary) !important;
        background-color: var(--field_bg) !important;
        border-color: var(--field_border) !important;
      }
    }
  }

  &-list {
    overflow: auto;
    max-height: 50vh;
  }

  &-nodata {
    text-align: center;
    padding: 8px;
  }

  &-item {
    margin-top: 8px;
    padding: 8px;
    border-radius: var(--border-radius);
    background-color: var(--bg_panel_primary) !important;
    cursor: pointer;
    position: relative;

    &:hover {
      background-color: rgba(var(--accent_primary), 0.2) !important;
    }

    &.one-active {
      background-color: rgba(var(--accent_primary), 0.3) !important;
    }

    &.diff-active {
      background-color: rgba(var(--accent_primary), 0.3) !important;

      &:after {
        content: "1";
        font-size: 12px;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 8px;
        border-radius: 50%;
        border: 1px solid rgba(255, 255, 255, 1);
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--accent_primary);
        color: rgba(255, 255, 255, 1);
      }

      &-first {
        &:after {
          content: "1";
        }
      }

      &-second {
        &:after {
          content: "2";
        }
      }
    }

    &-info {
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
}

.sm-fields {
  margin-top: 32px;

  &-container {
    .r-select {
      width: 100%;
    }
  }
}
</style>
